/* eslint-disable no-bitwise */
/**
 * SHA-256 synchrone, déterministe, sans dépendance externe.
 * Retourne un hash hexadécimal.
 */

function rotr(x: number, n: number) {
  return (x >>> n) | (x << (32 - n))
}

function add(...nums: number[]): number {
  let out = 0
  for (const n of nums) out = (out + n) >>> 0
  return out
}

function padSha256(input: Uint8Array) {
  const bitLen = BigInt(input.length) * 8n
  const withOne = input.length + 1
  const padLen = (64 - ((withOne + 8) % 64)) % 64
  const out = new Uint8Array(withOne + padLen + 8)

  out.set(input, 0)
  out[input.length] = 0x80

  // longueur sur 64 bits big-endian
  for (let i = 0; i < 8; i++) {
    out[out.length - 1 - i] = Number((bitLen >> BigInt(i * 8)) & 0xffn)
  }
  return out
}

function sha256Hex(messageBytes: Uint8Array): string {
  const K = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ]

  const H = [
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]

  const bytes = padSha256(messageBytes)
  const w = new Uint32Array(64)

  for (let offset = 0; offset < bytes.length; offset += 64) {
    for (let i = 0; i < 16; i++) {
      const j = offset + i * 4
      w[i] = ((bytes[j] << 24) | (bytes[j + 1] << 16) | (bytes[j + 2] << 8) | bytes[j + 3]) >>> 0
    }

    for (let i = 16; i < 64; i++) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3)
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10)
      w[i] = add(w[i - 16], s0, w[i - 7], s1)
    }

    let [a, b, c, d, e, f, g, h] = H

    for (let i = 0; i < 64; i++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)
      const ch = (e & f) ^ (~e & g)
      const temp1 = add(h, S1, ch, K[i], w[i])
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)
      const maj = (a & b) ^ (a & c) ^ (b & c)
      const temp2 = add(S0, maj)

      h = g
      g = f
      f = e
      e = add(d, temp1)
      d = c
      c = b
      b = a
      a = add(temp1, temp2)
    }

    H[0] = add(H[0], a)
    H[1] = add(H[1], b)
    H[2] = add(H[2], c)
    H[3] = add(H[3], d)
    H[4] = add(H[4], e)
    H[5] = add(H[5], f)
    H[6] = add(H[6], g)
    H[7] = add(H[7], h)
  }

  return H.map((x) => x.toString(16).padStart(8, "0")).join("")
}

export function hash(value: string, { salt = "" } = {}): string {
  const input = `${salt}${String(value)}`.normalize("NFC")
  const bytes = new TextEncoder().encode(input)
  return sha256Hex(bytes)
}
